import {
    getContractWarnings,
    getPOCServiceWarnings,
} from '@/components/AlertNotification/services'
import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    ContractWarning,
    POCServiceWarning,
    WarningItem,
} from '@/modules/Util/components/Alerts/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import {
    AlertTriangle,
    ArrowRight,
    Clock,
    FileText,
    Package,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'

type AlertTab = 'poc' | 'contract'

const SalesAlertsWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<AlertTab>('poc')

    // Fetch POC warnings
    const { data: pocData, isLoading: isPocLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.POC_SERVICE_WARNINGS],
        queryFn: getPOCServiceWarnings,
    })

    // Fetch Contract warnings
    const { data: contractData, isLoading: isContractLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.CONTRACT_WARNINGS],
            queryFn: getContractWarnings,
        })

    const isLoading = isPocLoading || isContractLoading

    // Process POC data
    const pocWarnings = pocData?.results || []
    const pocOverdue = pocWarnings.filter(
        (w) => w.type === 'poc_service_overdue'
    )
    const pocUpcoming = pocWarnings.filter(
        (w) => w.type === 'poc_service_upcoming'
    )

    // Process Contract data
    const contractWarnings = contractData?.results || []
    const contractExpired = contractWarnings.filter(
        (w) => w.type === 'contract_expired'
    )
    const contractExpiring = contractWarnings.filter(
        (w) => w.type === 'contract_expiring'
    )

    // Tab configuration
    const tabs = useMemo(
        () => [
            {
                key: 'poc' as AlertTab,
                label: 'POC',
                count: pocWarnings.length,
            },
            {
                key: 'contract' as AlertTab,
                label: t('alerts.contracts'),
                count: contractWarnings.length,
            },
        ],
        [pocWarnings.length, contractWarnings.length, t]
    )

    // Get current warnings based on active tab
    const currentWarnings = activeTab === 'poc' ? pocWarnings : contractWarnings
    const currentOverdue = activeTab === 'poc' ? pocOverdue : contractExpired
    const currentUpcoming = activeTab === 'poc' ? pocUpcoming : contractExpiring

    const handlePocClick = (serviceId: number) => {
        navigate(
            generatePathDatacentral(paths.serviceDetail, { id: serviceId })
        )
    }

    const handleContractClick = (contractId: number) => {
        navigate(
            generatePathDatacentral(paths.contractDetail, { id: contractId })
        )
    }

    const handleViewAll = () => {
        if (activeTab === 'poc') {
            navigate(
                generatePathDatacentral(
                    paths.services,
                    {},
                    { tab: 'service-poc' }
                )
            )
        } else {
            navigate(generatePathDatacentral(paths.customerContractList, {}))
        }
    }

    const renderWarningItem = (warning: WarningItem) => {
        if (activeTab === 'poc') {
            const pocDataItem = warning.data as POCServiceWarning
            const isOverdue = warning.type === 'poc_service_overdue'

            return (
                <div
                    key={warning.id}
                    onClick={() => handlePocClick(pocDataItem.service_id)}
                    className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                            className={`p-1.5 rounded-full flex-shrink-0 ${isOverdue ? 'bg-red-100' : 'bg-orange-100'}`}
                        >
                            <Package
                                className={`w-3.5 h-3.5 ${isOverdue ? 'text-red-600' : 'text-orange-600'}`}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs text-gray-900 truncate">
                                {pocDataItem.service_name}
                            </div>
                            <div className="text-[10px] text-gray-500 truncate">
                                {pocDataItem.customer_name}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                isOverdue
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-orange-100 text-orange-700'
                            }`}
                        >
                            {Math.abs(pocDataItem.days_difference)}d
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                </div>
            )
        } else {
            const contractDataItem = warning.data as ContractWarning
            const isExpired = warning.type === 'contract_expired'

            return (
                <div
                    key={warning.id}
                    onClick={() =>
                        handleContractClick(contractDataItem.contract_id)
                    }
                    className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                            className={`p-1.5 rounded-full flex-shrink-0 ${isExpired ? 'bg-red-100' : 'bg-orange-100'}`}
                        >
                            <FileText
                                className={`w-3.5 h-3.5 ${isExpired ? 'text-red-600' : 'text-orange-600'}`}
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs text-gray-900 truncate">
                                {contractDataItem.contract_number_id}
                            </div>
                            <div className="text-[10px] text-gray-500 truncate">
                                {contractDataItem.customer_name}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                isExpired
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-orange-100 text-orange-700'
                            }`}
                        >
                            {isExpired
                                ? Math.abs(contractDataItem.days_overdue)
                                : contractDataItem.days_to_expiry}
                            d
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                </div>
            )
        }
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <Typogaphy className="font-semibold text-primary mb-3 flex-shrink-0">
                    {t('alerts.title')}
                </Typogaphy>

                {/* Tabs */}
                <div className="flex-shrink-0 mb-3">
                    <ButtonGroupCustomized
                        className="p-0.5"
                        active={activeTab}
                        onChange={(value) => setActiveTab(value as AlertTab)}
                        buttons={tabs.map((tab) => ({
                            label: tab.label,
                            value: tab.key,
                            count: tab.count,
                        }))}
                    />
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        <div className="grid grid-cols-2 gap-2">
                            <Skeleton className="h-16 rounded-lg" />
                            <Skeleton className="h-16 rounded-lg" />
                        </div>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div className="grid grid-cols-2 gap-2 flex-shrink-0 mb-3">
                            <div className="flex items-center gap-2 p-2.5 bg-red-50 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                                <div>
                                    <div className="text-lg font-bold text-red-600">
                                        {currentOverdue.length}
                                    </div>
                                    <div className="text-[10px] text-red-600">
                                        {activeTab === 'poc'
                                            ? t('widgets.overdue')
                                            : t('alerts.expired')}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-2.5 bg-orange-50 rounded-lg">
                                <Clock className="w-4 h-4 text-orange-600" />
                                <div>
                                    <div className="text-lg font-bold text-orange-600">
                                        {currentUpcoming.length}
                                    </div>
                                    <div className="text-[10px] text-orange-600">
                                        {activeTab === 'poc'
                                            ? t('widgets.upcoming')
                                            : t('alerts.expiring')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* List - Fixed 3 items */}
                        <div className="space-y-1.5 flex-1">
                            {currentWarnings.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t('alerts.no_alerts')}
                                </div>
                            ) : (
                                <>
                                    {currentWarnings
                                        .slice(0, 3)
                                        .map((warning) =>
                                            renderWarningItem(warning)
                                        )}
                                    {/* Empty placeholders */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            3 - currentWarnings.length
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                                        />
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All */}
                        {currentWarnings.length > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} (
                                    {currentWarnings.length})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default SalesAlertsWidget
